import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdAdd, MdEdit, MdDelete, MdCheckCircle, MdCancel } from 'react-icons/md';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { Profile } from '../../types/permissions';
import { Layout } from '../../components/Layout';
import { PageTitle } from '../../components/PageTitle';
import { Loading } from '../../components/Loading';
import { Modal } from '../../components/Modal';

export function Profiles() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  async function loadProfiles() {
    try {
      setLoading(true);
      const response = await api.get('/profiles');
      setProfiles(response.data);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar perfis',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProfiles();
  }, []);

  async function handleDelete(profile: Profile) {
    try {
      await api.delete(`/profiles/${profile.id}`);
      enqueueSnackbar('Perfil deletado com sucesso!', { variant: 'success' });
      setDeleteModalOpen(false);
      setSelectedProfile(null);
      loadProfiles();
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao deletar perfil',
        { variant: 'error' }
      );
    }
  }

  function openDeleteModal(profile: Profile) {
    setSelectedProfile(profile);
    setDeleteModalOpen(true);
  }

  if (loading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <PageTitle title="Perfis de Acesso" />
          <Link
            to="/profiles/create"
            className="flex items-center gap-2 px-4 py-2 bg-[#0085BD] hover:bg-[#006a96] text-white rounded-lg transition-colors"
          >
            <MdAdd size={20} />
            Novo Perfil
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {profiles.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      Nenhum perfil cadastrado
                    </td>
                  </tr>
                ) : (
                  profiles.map(profile => (
                    <tr key={profile.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {profile.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-md">
                          {profile.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            profile.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {profile.is_active ? (
                            <>
                              <MdCheckCircle size={14} /> Ativo
                            </>
                          ) : (
                            <>
                              <MdCancel size={14} /> Inativo
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/profiles/edit/${profile.id}`}
                            className="text-[#0085BD] hover:text-[#006a96] transition-colors"
                            title="Editar"
                          >
                            <MdEdit size={20} />
                          </Link>
                          <button
                            onClick={() => openDeleteModal(profile)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Deletar"
                          >
                            <MdDelete size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Modal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedProfile(null);
          }}
          title="Deletar Perfil"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Tem certeza que deseja deletar o perfil{' '}
              <strong>{selectedProfile?.name}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Atenção: Esta ação não pode ser desfeita e todos os usuários com este
              perfil perderão suas permissões associadas.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setSelectedProfile(null);
                }}
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => selectedProfile && handleDelete(selectedProfile)}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Deletar
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
